import { useContext, useEffect, useMemo, useRef } from 'react';
import router from 'next/router';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { capitalize } from 'lodash';
import { ServerSideErrorModal, LoadingView } from '@web/components';
import {
  freshModalsContent,
  dryModalsContent,
} from '@web/testing/modalConstants';
import { getVariantsSortedByPrice, truncatePrice } from '../../products';
import { ApiResult, wrapErrorWithResult } from '../../api';
import { OnboardingIntro } from '../OnboardingIntro';
import { PupNameForm } from '../PupNameForm';
import { PupGenderForm } from '../PupGenderForm';
import { PupStatusForm } from '../PupStatusForm';
import { UserInfoForm } from '../UserInfoForm';
import { CaloriesForm } from '../CaloriesForm';
import { ActivityForm } from '../ActivityForm';
import { RecommendedMealPlanForm } from '../RecommendedPlanForm';
import planHeaderPhoto from '../../images/product-photos/pack-shots/pack-on-board-with-shadow.png';
import { QuizHeader } from '../QuizHeader';
import { CarouselMealPlanForm } from '../CarouselMealPlanForm';
import { OptimizedMealPlanForm } from '../OptimizedMealPlanForm';
import { AllergyForm } from '../AllergyForm';
import { useAllergies } from '../../allergies';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { calculateDiscountedPrice } from '../../offer/helpers';
import { AddOnSelectionForm } from '../AddOnSelectionForm';
import { ExtrasSelectionForm } from '../ExtrasSelectionForm';
import {
  recipes as freshRecipes,
  optimizedRecipes,
  dryRecipes,
  MEAL_PREP_TYPES,
} from '../constants';
import { OnboardingContext } from './context';
import { interpolations } from './interpolation';
import {
  onCartSubmission,
  getLowestCostVariant,
  getRecipeCombination,
  getUnavailableRecipes,
  getSelectedAllergies,
  normalize,
} from './functions';
import {
  STATE,
  ACTIONS,
  BC_TO_SHOPIFY_SKU_MAP,
  levelToLabel,
} from './constants';
import { Main, Wrapper } from './styles';
import {
  userSkipsExtraSelection,
  userContinuesWithExtraSelection,
} from './events';

const QuizContainer = ({
  stepNumber,
  children,
  steps = 7,
  showPromoBar = false,
  progress = true,
  overrideText = '',
  centerLogo = false,
}) => {
  const {
    showModal,
    setShowModal,
    flags: { skipCustomerInfo },
  } = useContext(OnboardingContext);
  return (
    <Wrapper>
      <QuizHeader
        showPromoBar={showPromoBar}
        progress={progress}
        overrideText={overrideText}
        currentStep={stepNumber}
        totalSteps={skipCustomerInfo ? steps - 1 : steps}
        centerLogo={centerLogo}
      />
      <Main>{children}</Main>
      <ServerSideErrorModal
        shown={showModal}
        headerText="Hmm, something's not right."
        errorMessage="We weren't able to complete your request, please refresh the page and try again."
        onCloseModal={() => setShowModal(false)}
      />
    </Wrapper>
  );
};

QuizContainer.propTypes = {
  steps: PropTypes.number,
  stepNumber: PropTypes.number.isRequired,
  children: PropTypes.object.isRequired,
  showPromoBar: PropTypes.bool,
  progress: PropTypes.bool,
  overrideText: PropTypes.string,
  centerLogo: PropTypes.string,
};

const IntroPage = props => {
  const offer = useShopifyOfferContext();
  const { send } = useContext(OnboardingContext);
  const goBack = () => router.push('/');

  return (
    <OnboardingIntro
      {...props}
      goBack={goBack}
      onContinue={() => send(ACTIONS.toPupName())}
      popUpText={interpolate(props.popUpText, { offer: offer.formattedAmount })}
    />
  );
};

const PupName = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack } =
    useContext(OnboardingContext);
  const defaultValues = {
    name: pet?.name,
  };

  const onSubmit = async ({ name }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ name });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toPupGender({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  return (
    <QuizContainer stepNumber={1}>
      <PupNameForm
        index={1}
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...props}
      />
    </QuizContainer>
  );
};

const PupGender = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack } =
    useContext(OnboardingContext);

  const defaultValues = {
    gender: pet?.gender,
  };
  const genderList = [
    { name: 'Female', value: 'Female' },
    { name: 'Male', value: 'Male' },
  ];
  const onSubmit = async ({ gender }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ gender });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toPupStatus({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 2,
  };

  return (
    <QuizContainer stepNumber={2}>
      <PupGenderForm
        genderList={genderList}
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PupStatus = props => {
  const { pet, savePet, send, reporter, setShowModal, goBack } =
    useContext(OnboardingContext);

  const defaultValues = {
    spayedStatus: pet?.is_neutered?.toString(),
  };

  const onSubmit = async ({ spayedStatus }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ is_neutered: spayedStatus });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toWeight({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 3,
  };

  return (
    <QuizContainer stepNumber={3}>
      <PupStatusForm
        defaultValues={defaultValues}
        goBack={goBack}
        onSubmit={onSubmit}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Weight = props => {
  const { pet, savePet, send, goBack, goToLogin, reporter, setShowModal } =
    useContext(OnboardingContext);
  const defaultValues = {
    weight: pet?.weight,
    idealWeight: pet?.ideal_weight,
  };

  const onSubmit = async ({ weight, idealWeight }) => {
    let result, payload;
    try {
      const response = await savePet({
        ideal_weight: idealWeight,
        weight,
      });
      payload = {
        action: 'update',
        pet: response.data.pet,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toActivity({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 4,
  };

  const isPuppy = false;

  return (
    <QuizContainer stepNumber={4}>
      <CaloriesForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
        isPuppy={isPuppy}
      />
    </QuizContainer>
  );
};

const Activity = props => {
  const { pet, savePet, send, goBack, goToLogin, reporter, setShowModal } =
    useContext(OnboardingContext);
  const defaultValues = {
    activity: pet?.activity_level,
  };
  const onSubmit = async ({ activity }) => {
    let result, payload;
    try {
      const response = await savePet({ activity_level: activity });

      payload = {
        action: 'update',
        pet: response.data.pet,
        pets: response.data.pets,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toAllergies({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 5,
  };
  return (
    <QuizContainer stepNumber={5}>
      <ActivityForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Allergies = props => {
  const {
    pet,
    cart,
    send,
    goBack,
    goToLogin,
    savePet,
    isLocalPet,
    reporter,
    customer,
    setShowModal,
    refreshExtras,
    flags: { skipCustomerInfo },
  } = useContext(OnboardingContext);

  const allAllergies = useAllergies();

  const hasPetAllergies = pet?.allergies && pet?.allergies.length > 0;

  const noAllergiesSelected = `pet:${pet?.id}:no_allergy`;

  const userSelectedNo =
    typeof window !== 'undefined' && localStorage.getItem(noAllergiesSelected);

  const selectedAllergies = useMemo(() => {
    return hasPetAllergies
      ? normalize(getSelectedAllergies(pet?.allergies, allAllergies))
      : [];
  }, [hasPetAllergies, pet?.allergies, allAllergies]);

  const defaultValues = useMemo(
    () => ({
      //* For allergy radio (yes/no)
      allergy: hasPetAllergies
        ? 'true'
        : pet?.starter_box || userSelectedNo
        ? 'false'
        : null,
      //* For allergy selection (multiselect)
      allergies: selectedAllergies,
    }),
    [hasPetAllergies, selectedAllergies, pet?.starter_box, userSelectedNo],
  );

  const onSubmit = async ({ allergy, allergies }) => {
    let result, payload;
    let allergyIds = undefined;

    if (allergy === 'true') {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(noAllergiesSelected);
      }

      allergyIds = allergies?.map(({ value, label }) =>
        isLocalPet ? { id: value, name: label } : value,
      );
    } else if (allergy === 'false') {
      if (typeof window !== 'undefined') {
        localStorage.setItem(noAllergiesSelected, 'true');
      }
    }

    try {
      //* Wait for meal plans to refetch since clearing allergies affects available recipes
      const response = await savePet(
        {
          allergy_ids:
            allergyIds === undefined || allergyIds.length === 0
              ? undefined
              : allergyIds,
        },
        { waitForMealPlans: true },
      );

      payload = {
        action: 'update',
        pet: response.data.pet,
        pets: response.data.pets,
        allergies: allergies?.map(({ label }) => label) || [],
        allergyIds: allergyIds,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        if (skipCustomerInfo || customer) {
          send(
            ACTIONS.toRecommendedPlan({
              customer,
              pet: payload.pet,
              pets: payload.pets,
              cart,
              flags: { skipCustomerInfo },
              ...payload,
            }),
          );
          return;
        }

        send(
          ACTIONS.toUserInfo({
            pet: payload.pet,
            pets: payload.pets,
            cart,
            flags: { skipCustomerInfo },
            ...payload,
          }),
        );
        //* Reload extras to account for allergies
        refreshExtras();
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });

    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 6,
  };

  return (
    <QuizContainer stepNumber={6}>
      <AllergyForm
        defaultValues={defaultValues}
        placeholderAllergiesCount={9}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const UserInfo = props => {
  const {
    customer,
    pet,
    send,
    attachCustomer,
    goToLogin,
    reporter,
    setShowModal,
    goBack,
    setShowPlanSelection,
    elevarReporter,
  } = useContext(OnboardingContext);

  const defaultValues = {
    firstName: customer?.first_name || '',
    email: customer?.email || '',
  };

  const onSubmit = async ({ firstName, email }) => {
    let result;

    try {
      //* Payload returns the entire cart object which includes the customer and pets
      let payload = await attachCustomer({
        ...(firstName ? { first_name: firstName } : {}),
        last_name: 'Fake-LastName',
        email,
      });

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    setShowPlanSelection(false);

    ApiResult.match(result, {
      success: async payload => {
        elevarReporter?.elevarSubscribe({
          email,
          userProperties: {
            customer_email: payload?.customer?.email || email,
            customer_id: payload?.customer?.id,
            customer_first_name:
              payload?.customer?.first_name || firstName || undefined,
          },
        });
        send(ACTIONS.toRecommendedPlan({ ...payload }));
      },
      error: {
        //* 404 Error (Session ID not found)
        //* 400 Error (Invalid payload)
        //* 422 Error (Internal error)
        client: error => reporter.error(error.original),
        //* 401 Error (Existing customer)
        existingCustomer: () => goToLogin(),
        //* 500 error (All other errors)
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 0,
  };

  return (
    <QuizContainer
      progress={false}
      stepNumber={7}
      overrideText={'Save Your Info'}
    >
      <UserInfoForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        petName={pet?.name}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const getUnavailableMealPrepTypes = combinationsByPlanType => {
  return MEAL_PREP_TYPES.filter(type => {
    const planType = combinationsByPlanType[type];
    return (
      !planType ||
      !planType.planCombinations ||
      planType.planCombinations.length === 0
    );
  });
};

const RecommendedPlan = props => {
  const {
    setShowModal,
    reporter,
    elevarReporter,
    combinationsByPlanType,
    pet,
    getPetPronoun,
    pet_id: petId,
    cartApi,
    send,
    service,
    pricePeriod,
    goBack,
    setLocalSelection,
    mealPlanForPet,
    flags: { onboardingFlowUI, pdpMealPlanTest, hideMixed },
    campaignId,
  } = useContext(OnboardingContext);
  const { type, amount, formattedAmount } = useShopifyOfferContext();
  let combinationId = null;
  const quizCompleteLogged = useRef(false);
  const defaultPlanType = mealPlanForPet
    ? mealPlanForPet.plan_type_name
    : 'fresh';
  const defaultFreshRecipes = mealPlanForPet
    ? mealPlanForPet.recipe_selections.fresh
    : combinationsByPlanType.fresh?.recommendedCombination?.recipes?.fresh ??
      [];
  const defaultBakedRecipes =
    mealPlanForPet?.plan_type_name === 'mixed'
      ? mealPlanForPet.recipe_selections.baked
      : combinationsByPlanType.mixed?.recommendedCombination?.recipes?.baked ??
        [];

  const unavailableFreshRecipes = getUnavailableRecipes(
    combinationsByPlanType.fresh?.availableRecipes?.fresh || [],
  );
  const unavailableBakedRecipes = getUnavailableRecipes(
    combinationsByPlanType.baked?.availableRecipes?.baked || [],
  );

  const unavailableMealPrepTypes = getUnavailableMealPrepTypes(
    combinationsByPlanType,
  );

  const getConfigFromRecipes = (
    selectedMealPrepType,
    selectedFreshRecipes,
    selectedBakedRecipes,
  ) => {
    if (
      selectedMealPrepType === 'fresh' ||
      selectedMealPrepType === 'half_fresh'
    ) {
      const product = combinationsByPlanType[selectedMealPrepType];

      if (!selectedFreshRecipes.length)
        return { price: null, message: 'Choose one or more recipes.' };

      const variants =
        product && Array.isArray(product.planCombinations)
          ? getVariantsSortedByPrice(product.planCombinations)
          : [];
      const newVariant = getRecipeCombination(variants, {
        fresh: selectedFreshRecipes,
        baked: [],
      });

      if (!newVariant)
        return {
          price: null,
          message: 'No matching recipe combination found.',
        };

      const price = newVariant.price_per_week;
      const discountedPrice = calculateDiscountedPrice(price, type, amount);
      combinationId = newVariant.combination_id;

      return {
        price,
        discountedPrice,
        combination_id: newVariant.combination_id,
        discountAmount: amount,
      };
    } else {
      const product = combinationsByPlanType['mixed'];

      if (!selectedFreshRecipes.length)
        return {
          price: null,
          message: 'Choose one or more fresh recipes.',
        };

      if (!selectedBakedRecipes.length)
        return {
          price: null,
          message: 'Choose one or more baked recipes.',
        };

      const variants = getVariantsSortedByPrice(product.planCombinations);
      const newVariant = getRecipeCombination(variants, {
        fresh: selectedFreshRecipes,
        baked: selectedBakedRecipes,
      });

      if (!newVariant)
        return {
          price: null,
          message: 'No matching recipe combination found.',
        };

      const price = newVariant.price_per_week;
      const discountedPrice = calculateDiscountedPrice(price, type, amount);
      combinationId = newVariant.combination_id;

      return {
        price,
        discountedPrice,
        combination_id: newVariant.combination_id,
        discountAmount: amount,
      };
    }
  };
  const lowestMixedVariant = combinationsByPlanType?.mixed?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.mixed.planCombinations)
    : null;
  let lowestFreshVariant = combinationsByPlanType?.fresh?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.fresh.planCombinations)
    : null;
  const lowestHalfFreshVariant = combinationsByPlanType?.half_fresh
    ?.planCombinations?.length
    ? getLowestCostVariant(combinationsByPlanType.half_fresh.planCombinations)
    : null;
  const lowestBakedVariant = combinationsByPlanType?.dry?.planCombinations
    ?.length
    ? getLowestCostVariant(combinationsByPlanType.dry.planCombinations)
    : null;

  const productTypeInterpolationSubstitutes = {
    freshPricePerWeek: lowestFreshVariant?.price_per_week,
    freshDiscountPricePerWeek: calculateDiscountedPrice(
      lowestFreshVariant?.price_per_week,
      type,
      amount,
    ),
    mixedPricePerWeek: lowestMixedVariant?.price_per_week,
    mixedDiscountPricePerWeek: calculateDiscountedPrice(
      lowestMixedVariant?.price_per_week,
      type,
      amount,
    ),
    halfFreshPricePerWeek: lowestHalfFreshVariant?.price_per_week,
    halfFreshDiscountPricePerWeek: calculateDiscountedPrice(
      lowestHalfFreshVariant?.price_per_week,
      type,
      amount,
    ),
    bakedPricePerWeek: lowestBakedVariant?.price_per_week,
    bakedDiscountPricePerWeek: calculateDiscountedPrice(
      lowestBakedVariant?.price_per_week,
      type,
      amount,
    ),
  };

  const onMealPlanAndRecipesSubmit = async (
    planType,
    freshRecipes,
    bakedRecipes,
  ) => {
    const selectedPlanDefaultCadence =
      combinationsByPlanType[planType]?.cadence;

    if (planType === 'mixed') {
      send(
        ACTIONS.selectedMixedRecipes(
          {
            recipes: {
              dry: bakedRecipes,
              fresh: freshRecipes,
              planType: planType,
            },
          },
          {
            combinationsByPlanType,
            combinationId,
            cartProduct: mealPlanForPet,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
            campaignId,
            defaultCadence: selectedPlanDefaultCadence,
          },
        ),
      );
    } else {
      send(
        ACTIONS.selectedFreshRecipes(
          { recipes: freshRecipes, planType: planType },
          {
            combinationsByPlanType,
            combinationId,
            cartProduct: mealPlanForPet,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
            campaignId,
            defaultCadence: selectedPlanDefaultCadence,
          },
        ),
      );
    }
    return onCartSubmission(service);
  };
  const mealplanProps = {
    onSubmit: onMealPlanAndRecipesSubmit,
    headline: interpolate(props.headline, interpolations(pet)),
    subheadline: `We created a meal plan just for ${capitalize(pet.name)}—a ${
      pet.weight
    }-pound ${levelToLabel[pet.activity_level]} pup—${
      pet.allergies.length > 0
        ? `and ensured no ${pet.allergies
            .map(a => a.name.toLowerCase())
            .join(', ')} was in ${getPetPronoun(
            3,
            'POSSESSIVE_ADJECTIVE',
          )} recipes `
        : ''
    }so ${getPetPronoun(
      3,
      'SUBJECT',
    )} gets the perfect portions to stay healthy, happy, and thriving.`,
    petName: pet?.name,
    ctaText: interpolate(props.ctaText, { offer: formattedAmount }),
    modals: [...freshModalsContent, ...dryModalsContent],
    freshRecipes:
      onboardingFlowUI === 'optimized_mealprep_extras_ui'
        ? optimizedRecipes
        : freshRecipes,
    defaultFreshRecipes: defaultFreshRecipes ?? [],
    bakedRecipes: dryRecipes,
    defaultBakedRecipes: defaultBakedRecipes ?? [],
    defaultMealPrepType: defaultPlanType,
    mealPrepTypes: interpolate(
      props.mealPrepTypes,
      productTypeInterpolationSubstitutes,
    ),
    collapsedMealPrepTypes: interpolate(
      props.collapsedMealPrepTypes,
      productTypeInterpolationSubstitutes,
    ),
    unavailableMealPrepTypes: unavailableMealPrepTypes,
    unavailableFreshRecipes: unavailableFreshRecipes,
    unavailableBakedRecipes: unavailableBakedRecipes,
    onBackButtonClick: goBack,
    period: pricePeriod,
    getConfigFromRecipes: getConfigFromRecipes,
    body: props.body,
    headerImage: planHeaderPhoto,
    modalContent: interpolate(
      props.mealPlanModalContent,
      productTypeInterpolationSubstitutes,
    ),
    notSelectableText: `Based on your answers, this meal plan is unavailable for ${pet.name}.`,
    ctaButtonVariant: 'primary',
    hideMixed: hideMixed,
  };

  const carouselMealPlanProps = {
    ...mealplanProps,
    subheadline: props.subheadline,
    description: `Based on ${capitalize(pet.name)}'s ${
      pet.weight
    } lb size and ${
      levelToLabel[pet.activity_level]
    } lifestyle, we've created a fresh meal plan made with real ingredients${
      pet.allergies.length > 0
        ? ` (and zero ${pet.allergies
            .map(a => a.name.toLowerCase())
            .join(', ')}) `
        : ' '
    }to support ${pet.gender === 'Female' ? 'her' : 'his'} health every day.`,
  };

  // Only fire quiz complete for new pets, not edits
  useEffect(() => {
    if (quizCompleteLogged.current) return;
    if (!petId || mealPlanForPet) return;
    if (typeof elevarReporter?.elevarCompleteQuiz !== 'function') return;
    try {
      elevarReporter.elevarCompleteQuiz();
      quizCompleteLogged.current = true;
    } catch (error) {
      reporter?.error?.(error, { contexts: { elevar: 'completeQuiz' } });
    }
  }, [elevarReporter, reporter, petId, mealPlanForPet]);

  return pdpMealPlanTest === 'pdp_meal_plan' ? (
    <QuizContainer progress={false} centerLogo={true}>
      <CarouselMealPlanForm
        {...carouselMealPlanProps}
        carousel={props.carousel}
      />
    </QuizContainer>
  ) : onboardingFlowUI === 'optimized_mealprep_extras_ui' ? (
    <QuizContainer progress={false} centerLogo={true}>
      <OptimizedMealPlanForm
        desktopImage={props.desktopImage}
        {...mealplanProps}
      />
    </QuizContainer>
  ) : (
    <QuizContainer progress={false} overrideText={'Your plan'}>
      <RecommendedMealPlanForm {...mealplanProps} />
    </QuizContainer>
  );
};

RecommendedPlan.displayName = 'RecommendedPlan';
RecommendedPlan.propTypes = {};

const Extras = props => {
  const {
    reporter,
    goBack,
    setShowModal,
    pet,
    pet_id: petId,
    attachExtras,
    send,
    pets,
    selectedAddons,
    treatProducts,
    customer,
    supplementProducts,
    dentalChews,
    treatsForLife,
    flags: { onboardingFlowUI, skipCustomerInfo },
  } = useContext(OnboardingContext);
  const { formattedAmount, type, amount } = useShopifyOfferContext();
  const treatsAndDentalChews = [...treatProducts, ...dentalChews];

  const findProductBySku = (products, sku) => {
    const product = products.find(product => product.sku === sku);
    return product;
  };
  //If a person goes backwards to edit their allergies, we want to filter out the selected allergy from default
  const defaultAddons =
    selectedAddons?.filter(addon => {
      const mappedSku = BC_TO_SHOPIFY_SKU_MAP[addon.sku] || addon.sku;
      return findProductBySku(
        [...supplementProducts, ...treatsAndDentalChews],
        mappedSku,
      );
    }) || [];

  const unavailableAddons =
    selectedAddons?.filter(addon => {
      const mappedSku = BC_TO_SHOPIFY_SKU_MAP[addon.sku] || addon.sku;
      return !findProductBySku(
        [...supplementProducts, ...treatsAndDentalChews],
        mappedSku,
      );
    }) || [];

  const mergedAddonSection = props.addonSections.map((section, i) => {
    const addons = section.addons
      .map(addon => {
        const productType = addon.productType;
        const products =
          productType === 'TR' || productType === 'DC'
            ? treatsAndDentalChews
            : supplementProducts;
        const mappedSku = BC_TO_SHOPIFY_SKU_MAP[addon.sku] || addon.sku;
        const product = findProductBySku(products, mappedSku);
        if (!product) {
          return null;
        }

        const discountedPrice = calculateDiscountedPrice(
          product.price,
          type,
          amount,
        );

        return interpolate(addon, {
          addonPrice: truncatePrice(product.price),
          addonDiscount: truncatePrice(discountedPrice),
          petName: pet.name,
          addonSavePercentage: formattedAmount,
        });
      })
      .filter(Boolean);

    return {
      ...section,
      addons,
    };
  });

  const mergedModals = props.modals
    .map(modal => {
      const product = findProductBySku(
        [...supplementProducts, ...treatsAndDentalChews],
        BC_TO_SHOPIFY_SKU_MAP[modal.sku] || modal.sku,
      );
      if (!product) return null;

      const discountedPrice = calculateDiscountedPrice(
        product.price,
        type,
        amount,
      );

      return interpolate(modal, {
        addonPrice: truncatePrice(product.price),
        addonDiscount: truncatePrice(discountedPrice),
        addonSavePercentage: formattedAmount,
      });
    })
    .filter(Boolean);

  const onSubmit = async selectedAddons => {
    let result = ApiResult.success();
    const attachResult = await attachExtras(
      petId,
      selectedAddons.map(addon => ({
        sku: BC_TO_SHOPIFY_SKU_MAP[addon.sku] || addon.sku,
        frequency: addon.frequency,
        quantity: addon.quantity,
        recurrence: addon.recurrence,
      })),
    );

    result = ApiResult.success(attachResult)
      ? attachResult
      : ApiResult.error.client('Failed to attach extras', attachResult.error);

    ApiResult.match(result, {
      success: () => {
        reporter.tag(userContinuesWithExtraSelection(selectedAddons));
        if (skipCustomerInfo || customer) {
          send(
            ACTIONS.toCheckout({
              pet,
              pets,
              extras: selectedAddons,
              flags: { skipCustomerInfo },
            }),
          );
        } else {
          send(
            ACTIONS.toUserInfo({
              pet,
              extras: selectedAddons,
              pets,
              flags: { skipCustomerInfo },
            }),
          );
        }
      },
      error: {
        client: error => {
          reporter.error(error.original);
          setShowModal(true);
        },
        server: () => setShowModal(true),
      },
    });

    return result;
  };
  const onSkip = event => {
    event.preventDefault();
    if (selectedAddons.some(addon => unavailableAddons.includes(addon))) {
      onSubmit(
        selectedAddons.filter(addon => !unavailableAddons.includes(addon)),
      );
    }
    reporter.tag(userSkipsExtraSelection());
    if (skipCustomerInfo || customer) {
      send(ACTIONS.toCheckout({ pet, pets, flags: { skipCustomerInfo } }));
    } else {
      send(ACTIONS.toUserInfo({ pet, pets, flags: { skipCustomerInfo } }));
    }
  };

  return (
    <QuizContainer
      overrideText={'Your plan'}
      progress={false}
      centerLogo={true}
    >
      {onboardingFlowUI === 'optimized_mealprep_extras_ui' ? (
        <ExtrasSelectionForm
          headline={interpolate(props.headline, { pupName: pet.name })}
          subheadline={interpolate(props.subheadline, {
            pupName: pet.name,
          })}
          addonSections={mergedAddonSection}
          defaultAddons={defaultAddons}
          unavailableAddons={unavailableAddons}
          onSubmit={onSubmit}
          onSkipButtonClick={onSkip}
          onBackButtonClick={goBack}
          submitButtonText={props.desktopCtaText}
          modals={mergedModals}
          ctaButtonVariant="primary"
          centerSkip={true}
          treatsForLife={treatsForLife}
          pillText={interpolate(props.pillText, { offer: formattedAmount })}
          pillVariant={props.pillVariant}
          body={props.body}
        />
      ) : (
        <AddOnSelectionForm
          headline={interpolate(props.headline, { pupName: pet.name })}
          addonSections={mergedAddonSection}
          defaultAddons={defaultAddons}
          unavailableAddons={unavailableAddons}
          onSubmit={onSubmit}
          onSkipButtonClick={onSkip}
          onBackButtonClick={goBack}
          submitButtonText={props.desktopCtaText}
          modals={mergedModals}
          ctaButtonVariant="primary"
          centerSkip={true}
          treatsForLife={treatsForLife}
        />
      )}
    </QuizContainer>
  );
};

Extras.propTypes = {
  headline: PropTypes.string,
  addonSections: PropTypes.array,
  desktopCtaText: PropTypes.string,
  modals: PropTypes.array,
};

const Checkout = () => {
  const { cart, customer } = useContext(OnboardingContext);
  const checkoutUrl = cart?.checkout_url;
  const customerEmail = customer?.email;

  useEffect(() => {
    // To maintain user continuity between domains, send the anonymousId to our Shopify Segment instance
    let anonymousId = '';
    if (window.analytics && window.analytics.user) {
      anonymousId = window.analytics.user().anonymousId();
    }
    if (checkoutUrl) {
      const url = checkoutUrl?.split('?')?.[0];
      const search = window.location?.search?.split('?')?.[1] || '';
      const initialParams = (search && `&${search}`) || '';
      const anonymousIdParam =
        (anonymousId && `&segmentAnonymousId=${anonymousId}`) || '';
      const checkoutEmailParam =
        (customerEmail &&
          `&checkout[email]=${encodeURIComponent(customerEmail)}`) ||
        '';
      // There's always at minimum 1 query parameter (key) in a Shopify checkout url
      const queryParams =
        checkoutUrl.split('?')?.[1] +
        initialParams +
        anonymousIdParam +
        checkoutEmailParam;
      window.location.href = url + `?${queryParams}`;
    }
  }, [customerEmail, checkoutUrl]);
  return <LoadingView variant="checkout" />;
};

export const components = {
  [STATE.intro]: IntroPage,
  [STATE.pupName]: PupName,
  [STATE.pupGender]: PupGender,
  [STATE.pupStatus]: PupStatus,
  [STATE.weight]: Weight,
  [STATE.activity]: Activity,
  [STATE.allergies]: Allergies,
  [STATE.userInfo]: UserInfo,
  [STATE.recommendedPlan]: RecommendedPlan,
  [STATE.extras]: Extras,
  [STATE.checkout]: Checkout,
};
